/**
 * English copy for Space Manage → Members (`spaces.manage.members.*`).
 * Kept out of `core.ts` (already large); spliced in under `spaces.manage`.
 */
export const spaceManageMembers = {
  title: 'Members',
  subtitle: 'Browse, search, and moderate current, banned, and former members.',
  status: {
    label: 'Show',
    active: 'Current',
    banned: 'Banned',
    former: 'Former',
  },
  searchLabel: 'Search members',
  searchPlaceholder: 'Username, nickname, or ID',
  roleFilter: 'Role',
  roleFilterAll: 'All roles',
  roleFilterPlaceholder: 'Filter by role',
  roleFilterEmpty: 'No matching roles.',
  temporaryOnly: 'Temporary access only',
  reasonFilter: 'How they left',
  reasonFilterAll: 'Any reason',
  sortLabel: 'Sort',
  sort: {
    active: {
      recent_desc: 'Newest members',
      recent_asc: 'Oldest members',
    },
    banned: {
      recent_desc: 'Recently banned',
      recent_asc: 'Banned longest ago',
    },
    former: {
      recent_desc: 'Recently left',
      recent_asc: 'Left longest ago',
    },
    name_asc: 'Username A–Z',
    name_desc: 'Username Z–A',
  },
  showing: 'Showing {{from}}–{{to}} of {{total}}',
  showingCapped: 'Showing {{from}}–{{to}} of {{total}}+',
  showingUnknown: 'Showing {{from}}–{{to}}',
  pagePrev: 'Previous',
  pageNext: 'Next',
  loadError: "Couldn't load members.",
  empty: {
    active: 'No members match.',
    banned: 'No banned members match.',
    former: 'No former members match.',
  },
  badges: {
    owner: 'Owner',
    you: 'You',
    temporary: 'Temporary',
    banned: 'Banned',
    former: 'Former',
  },
  joined: 'Joined {{when}}',
  bannedOn: 'Banned {{when}}',
  banUntil: 'until {{when}}',
  banPermanent: 'permanently',
  banExpired: 'ban expired {{when}}',
  leftOn: 'Left {{when}}',
  temporaryUntil: 'Access ends {{when}}',
  departure: {
    left: 'Left on their own',
    kicked: 'Kicked',
    expired: 'Temporary access ended',
  },
  notesCount_one: '1 note',
  notesCount_other: '{{count}} notes',
  moreRoles: '+{{count}}',
  openMember: 'Open {{name}}',
  detail: {
    back: 'All members',
    notFound: 'This person has never been a member of this Space.',
    loadError: "Couldn't load this member.",
    copyId: 'Copy ID',
    copied: 'ID copied.',
    copyUsername: 'Copy username',
    usernameCopied: 'Username copied.',
    copyFailed: "Couldn't copy to the clipboard.",
    facts: 'Membership',
    joined: 'Joined',
    left: 'Left',
    removedBy: 'Removed by',
    removalReason: 'Kick reason',
    bannedAt: 'Banned',
    banExpires: 'Ban ends',
    banReason: 'Ban reason',
    temporaryAccess: 'Access ends',
    nickname: 'Nickname',
    formerRoles: 'Roles when they left',
    none: 'None',
  },
  actions: {
    title: 'Moderation',
    kick: 'Kick',
    ban: 'Ban',
    unban: 'Lift ban',
    kickTitle: 'Kick {{name}}?',
    kickBody: 'They can rejoin later if the Space allows it.',
    kickReasonOptional: 'Reason (optional, visible to moderators)',
    banTitle: 'Ban {{name}}?',
    banFormerBody: 'They already left. The ban stops them from rejoining.',
    unbanTitle: 'Lift the ban on {{name}}?',
    unbanBody: 'They will be restored as a member with the default role.',
    unbanFormerBody: 'They left before being banned, so they will be able to join again but are not added back.',
    kicked: 'Member kicked.',
    banned: 'Member banned.',
    unbanned: 'Ban lifted.',
    failed: 'That did not work.',
    rolesRankLocked: 'You can only change roles ranked below your own.',
    rolesSaved: 'Roles updated.',
    nicknameTitle: 'Nickname',
    nicknamePlaceholder: 'No nickname',
    nicknameSave: 'Save',
    nicknameClear: 'Clear',
    nicknameSaved: 'Nickname updated.',
    noActions: 'You have no moderation permissions for this member.',
  },
  roles: {
    title: 'Roles & permissions',
    current: 'Roles',
    noRoles: 'Only the Everyone role.',
    add: 'Add a role',
    addPlaceholder: 'Search roles to add…',
    addEmpty: 'No matching roles.',
    groupAvailable: 'Roles',
    groupLocked: 'You cannot assign',
    remove: 'Remove {{role}}',
    restore: 'Keep {{role}}',
    pending: {
      added: '(will be added)',
      removed: '(will be removed)',
    },
    permissions: 'Permissions',
    permissionsHint:
      "What these roles grant Space-wide, including Everyone. A channel or category can replace a role's permissions there, for example a read-only channel.",
    adminHint: 'Admin grants every permission.',
    noPermissions: 'No permissions.',
    grantedCount: '{{count}} granted',
    noChanges: 'No permission changes',
    addedCount: '+{{count}} gained',
    removedCount: '−{{count}} lost',
    withManage: '(+{{name}})',
    manageFlags: {
      manageNicknames: 'Manage Nicknames',
      manageMessages: 'Manage Messages',
    },
    reset: 'Reset',
    apply: 'Apply roles',
  },
  notes: {
    title: 'Moderator notes',
    hint: 'Only people with the Manage Members permission can see these.',
    placeholder: 'Add a note about this member…',
    add: 'Add note',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete this note?',
    edited: 'edited',
    empty: 'No notes yet.',
    loadMore: 'Older notes',
    loadError: "Couldn't load notes.",
    saveError: "Couldn't save the note.",
    limitReached: 'This member already has {{count}} notes. Delete one to add another.',
    by: '{{name}} · {{when}}',
    remaining: '{{count}} characters left',
  },
  history: {
    title: 'History',
    empty: 'No recorded moderation history.',
    loadMore: 'Older entries',
    loadError: "Couldn't load history.",
  },
};
